import axios from 'axios';
import { getLastSchoolSlug, clearLastSchoolSlug } from '@/lib/schoolSession';

// Use relative paths to avoid cross-origin issues with custom domains
// The Emergent ingress routes /api/* to the backend regardless of hostname
export const API = '/api';

// Ensure no axios call uses withCredentials (proxy overrides CORS headers with *)
axios.defaults.withCredentials = false;

// Add Authorization header to ALL axios requests globally (covers raw axios calls too)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('yukti_session_token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

/**
 * Axios instance with retry logic for transient errors (502, 503, 520, etc.)
 */
const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Request interceptor: add Authorization header from localStorage as iOS cookie fallback
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('yukti_session_token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second initial delay
const RETRYABLE_STATUS_CODES = [502, 503, 504, 520, 521, 522, 523, 524];

// Helper function to wait
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Request interceptor to add retry config
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Handle session expired — clear token and redirect to login.
    // School users are routed back to their branded /school/<slug>/login
    // when we have a cached school slug AND the current path is school-scoped.
    // Consumer (B2C) users land on the marketing page as before.
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      // Only redirect if not already on landing/auth pages to avoid loops
      if (path !== '/' && !path.includes('/auth') && !path.includes('/role')) {
        localStorage.removeItem('yukti_session_token');
        const slug = getLastSchoolSlug();
        const isSchoolScopedPath =
          path.startsWith('/school-admin') ||
          path.startsWith('/school-teacher') ||
          path.startsWith('/school-parent') ||
          path.startsWith('/student/') ||
          path.startsWith('/invites/accept') ||
          (!!slug && path.startsWith('/child/'));
        if (slug && isSchoolScopedPath) {
          window.location.href = `/school/${slug}/login`;
        } else {
          // Consumer flow — wipe any stale school slug so we don't bounce
          // a B2C user into a random school login on a future 401.
          clearLastSchoolSlug();
          window.location.href = '/';
        }
      }
      return Promise.reject(error);
    }

    // Handle trial expired - redirect to show locked screen
    if (error.response?.status === 403 && error.response?.data?.detail?.includes('Trial expired')) {
      // Don't redirect if already on a page that handles locked state
      const path = window.location.pathname;
      if (!path.includes('/family') && !path.includes('/child/')) {
        window.location.href = '/family';
      }
      return Promise.reject(error);
    }
    
    // Initialize retry count
    if (!config.__retryCount) {
      config.__retryCount = 0;
    }
    
    // Check if we should retry
    const shouldRetry = 
      config.__retryCount < MAX_RETRIES &&
      (
        !error.response || // Network error
        RETRYABLE_STATUS_CODES.includes(error.response.status)
      );
    
    if (shouldRetry) {
      config.__retryCount += 1;
      const retryDelay = RETRY_DELAY * config.__retryCount;
      
      console.log(`Retrying request (attempt ${config.__retryCount}/${MAX_RETRIES})...`);
      
      await delay(retryDelay);
      return api(config);
    }
    
    return Promise.reject(error);
  }
);

export default api;
